import { useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DbRoleWithActions,
  HandlersActionCatalogResponse,
  useDeleteApiProgramsByIdRolesAndRoleNameActionsActionNameMutation,
  useGetApiActionsQuery,
  useGetApiUsersRolesQuery,
  usePostApiProgramsByIdRolesAndRoleNameActionsMutation,
} from '../../../../app/athenaApiGen';

interface Props {
  open: boolean;
  role: DbRoleWithActions | null;
  onClose: () => void;
}

// Section grouping identical to AdminRolePermissionMappingPanel. Duplicated
// here until the section-derivation helper is extracted to a shared module
// (deferred pending merges of PRs #1674 + #1675).
const SITE_NAVIGATION = 'Site Navigation';
const PLATFORM_SETTINGS = 'Platform Settings';
const PROGRAM_SETTINGS = 'Program Settings';
const APPROVALS = 'Approvals';
const OTHER = 'Other';

const SECTION_ORDER = [
  SITE_NAVIGATION,
  'Projects',
  'Deliverables',
  'Forms',
  'Files',
  'Library',
  APPROVALS,
  PLATFORM_SETTINGS,
  PROGRAM_SETTINGS,
  OTHER,
];

const deriveSection = (slug: string, rawScope?: string | null): string => {
  if (
    slug === 'view-homepage' ||
    slug === 'view-approval-queue' ||
    slug === 'view-library' ||
    slug === 'view-ims-summary' ||
    slug === 'view-financial-views'
  ) {
    return SITE_NAVIGATION;
  }
  if (
    slug === 'admin-financial-plans' ||
    slug === 'admin-sitewide-access' ||
    slug.startsWith('admin-platform-')
  ) {
    return PLATFORM_SETTINGS;
  }
  if (
    slug === 'admin-ims' ||
    slug.startsWith('admin-manage-') ||
    slug.startsWith('admin-set-')
  ) {
    return PROGRAM_SETTINGS;
  }
  if (slug === 'cancel-submission') return 'Deliverables';
  if (
    slug === 'internally-approve-project' ||
    slug === 'internally-approve-deliverable' ||
    slug === 'internally-approve-tar' ||
    slug === 'federally-approve-deliverable' ||
    slug.startsWith('tar-approval-')
  ) {
    return APPROVALS;
  }
  switch (rawScope) {
    case 'Project':
      return 'Projects';
    case 'Deliverable':
      return 'Deliverables';
    case 'Deliverable Forms':
      return 'Forms';
    case 'Attachment':
      return 'Files';
    case 'Directory':
      return 'Library';
    case 'Workflow':
      return APPROVALS;
    default:
      return OTHER;
  }
};

const sectionRank = (section: string) => {
  const idx = SECTION_ORDER.indexOf(section);
  return idx === -1 ? SECTION_ORDER.length : idx;
};

const EditRolePermissionsDialog = ({ open, role, onClose }: Props) => {

  // Full action catalog — populates the section-grouped picker. Not
  // filtered by archived status: we want to reveal legacy actions too so
  // an admin can see and revoke them if needed.
  const { data: actionsData = [], isFetching: actionsLoading } =
    useGetApiActionsQuery({ includeArchived: true });

  // Fresh role list — used to invalidate the cache after save so the
  // Roles table and Role–Permission Mapping views pick up new state.
  // Also feeds the "used-in-any-role" set below so we can hide legacy
  // catalog entries no role in the program touches.
  const { data: rolesData = [], refetch: refetchRoles } =
    useGetApiUsersRolesQuery();

  // Union of every action slug currently granted to at least one role
  // in the program. Used by the "hide unused permissions" toggle to
  // strip legacy catalog entries no role touches. Reserved slugs the
  // role being edited *does* have (or has pending) always stay visible
  // so the diff summary never contradicts what the picker shows.
  const usedActionSlugs = useMemo(() => {
    const set = new Set<string>();
    rolesData.forEach((r) =>
      (r.actions ?? []).forEach((a) => {
        if (a?.action) set.add(a.action);
      }),
    );
    return set;
  }, [rolesData]);

  const [hideUnused, setHideUnused] = useState(true);

  const [addAction] = usePostApiProgramsByIdRolesAndRoleNameActionsMutation();
  const [removeAction] =
    useDeleteApiProgramsByIdRolesAndRoleNameActionsActionNameMutation();

  // Set of action slugs the role currently holds — pulled from the
  // `role.actions` payload the parent passed in. Baseline for the diff.
  const baselineActions = useMemo(() => {
    const set = new Set<string>();
    (role?.actions ?? []).forEach((a) => {
      if (a?.action) set.add(a.action);
    });
    return set;
  }, [role]);

  // Current selection in the dialog — resets to the baseline every time
  // the dialog opens with a new role, so canceling doesn't leak state.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setSelected(new Set(baselineActions));
  }, [open, baselineActions]);

  // Diff between baseline and selection: added slugs, removed slugs.
  const diff = useMemo(() => {
    const added: string[] = [];
    const removed: string[] = [];
    selected.forEach((s) => {
      if (!baselineActions.has(s)) added.push(s);
    });
    baselineActions.forEach((s) => {
      if (!selected.has(s)) removed.push(s);
    });
    added.sort();
    removed.sort();
    return { added, removed };
  }, [selected, baselineActions]);
  const hasChanges = diff.added.length > 0 || diff.removed.length > 0;

  // Grouped catalog by section, ordered per SECTION_ORDER then
  // alphabetical by slug within each section. Backend field names are
  // actionName / actionScope. When `hideUnused` is on we drop actions
  // no role in the program touches — but *always* keep anything the
  // current role has (or is pending) so the diff and picker never
  // disagree.
  const groupedActions = useMemo(() => {
    const grouped: Record<string, HandlersActionCatalogResponse[]> = {};
    actionsData.forEach((a) => {
      if (!a.actionName) return;
      if (
        hideUnused &&
        !usedActionSlugs.has(a.actionName) &&
        !baselineActions.has(a.actionName) &&
        !selected.has(a.actionName)
      ) {
        return;
      }
      const section = deriveSection(a.actionName, a.actionScope);
      (grouped[section] ||= []).push(a);
    });
    return Object.entries(grouped)
      .map(([section, items]) => ({
        section,
        items: items.sort((a, b) =>
          (a.actionName || '').localeCompare(b.actionName || ''),
        ),
      }))
      .sort((a, b) => sectionRank(a.section) - sectionRank(b.section));
  }, [actionsData, hideUnused, usedActionSlugs, baselineActions, selected]);

  const hiddenUnusedCount = useMemo(() => {
    if (!hideUnused) return 0;
    return actionsData.filter(
      (a) =>
        a.actionName &&
        !usedActionSlugs.has(a.actionName) &&
        !baselineActions.has(a.actionName) &&
        !selected.has(a.actionName),
    ).length;
  }, [actionsData, hideUnused, usedActionSlugs, baselineActions, selected]);

  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});
  const toggleSection = (section: string) =>
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const toggleAction = (slug: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Chi has a known bug decoding %2F in URL paths — role names
  // containing "/" (only Program Manager/Lead in prod today) can't be
  // edited via the /api/programs/{id}/roles/{roleName}/actions endpoint.
  // Disable save + surface the reason if the parent hands us one.
  const roleHasSlash = !!role?.roleName?.includes('/');

  const handleSave = async () => {
    if (!role?.roleName || !role?.programId || !hasChanges) return;
    if (roleHasSlash) {
      setSaveError(
        "Can't save: this role's name contains '/', which the API can't currently PATCH. Rename the role or wait for the backend fix.",
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Fire adds and removes in parallel — the backend enforces
      // idempotency on both. If any one call fails, the rest still land
      // and we surface the first error.
      const addOps = diff.added.map((slug) =>
        addAction({
          id: role.programId as string,
          roleName: role.roleName as string,
          handlersAddRoleActionRequest: { actionName: slug },
        }).unwrap(),
      );
      const removeOps = diff.removed.map((slug) =>
        removeAction({
          id: role.programId as string,
          roleName: role.roleName as string,
          actionName: slug,
        }).unwrap(),
      );
      const results = await Promise.allSettled([...addOps, ...removeOps]);
      const firstFail = results.find((r) => r.status === 'rejected');
      if (firstFail && firstFail.status === 'rejected') {
        const reason: any = firstFail.reason;
        throw new Error(
          reason?.data?.details || reason?.data?.error || 'Save failed',
        );
      }
      await refetchRoles();
      setSaveSuccess(true);
      onClose();
    } catch (e: any) {
      setSaveError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalCurrent = baselineActions.size;
  const totalAfter = selected.size;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Edit permissions
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              {role?.roleName ?? '—'}
              {role?.roleType ? ` · ${role.roleType}` : ''}
            </Typography>
          </Stack>
        </DialogTitle>

        <Divider />

        {/* Sticky diff summary — headline count + explicit chips per
            added/removed slug so the admin can see the exact pending
            change set without scrolling through the picker. */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            px: 3,
            py: 1.5,
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${totalCurrent} → ${totalAfter} permissions`}
            />
            {diff.added.length > 0 && (
              <Chip
                size="small"
                color="success"
                variant="filled"
                label={`+${diff.added.length}`}
                icon={<CheckIcon />}
              />
            )}
            {diff.removed.length > 0 && (
              <Chip
                size="small"
                color="error"
                variant="filled"
                label={`−${diff.removed.length}`}
                icon={<ClearIcon />}
              />
            )}
            {!hasChanges && (
              <Typography variant="caption" color="text.secondary">
                No changes yet
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideUnused}
                  onChange={(e) => setHideUnused(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Hide unused permissions
                </Typography>
              }
              sx={{ m: 0 }}
            />
            {hasChanges && (
              <Button
                size="small"
                variant="text"
                onClick={() => setSelected(new Set(baselineActions))}
                startIcon={<ClearIcon fontSize="small" />}
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
            )}
          </Stack>

          {diff.added.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'success.main',
                  fontWeight: 700,
                  minWidth: 60,
                }}
              >
                Adding
              </Typography>
              {diff.added.map((slug) => (
                <Chip
                  key={`add-${slug}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={slug}
                  onDelete={() => toggleAction(slug)}
                  deleteIcon={<ClearIcon />}
                  sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                />
              ))}
            </Stack>
          )}

          {diff.removed.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'error.main',
                  fontWeight: 700,
                  minWidth: 60,
                }}
              >
                Removing
              </Typography>
              {diff.removed.map((slug) => (
                <Chip
                  key={`rm-${slug}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  label={slug}
                  onDelete={() => toggleAction(slug)}
                  deleteIcon={<ClearIcon />}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    textDecoration: 'line-through',
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>

        <DialogContent dividers>
          {roleHasSlash && (
            <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
              This role's name contains a "/". The API can't currently PATCH
              roles with slashes in their names (chi decoded-slash bug). You can
              view what's changed but can't save yet.
            </Alert>
          )}

          {hideUnused && hiddenUnusedCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {hiddenUnusedCount} unused permission
              {hiddenUnusedCount === 1 ? '' : 's'} hidden — no role in the
              program has them. Toggle above to reveal.
            </Typography>
          )}

          {actionsLoading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading permission catalog…
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {groupedActions.map(({ section, items }) => {
                const grantedInSection = items.filter(
                  (a) => a.actionName && selected.has(a.actionName),
                ).length;
                const collapsed = !!collapsedSections[section];
                return (
                  <Box key={section}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSection(section)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleSection(section);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        mb: 0.5,
                      }}
                    >
                      {collapsed ? (
                        <KeyboardArrowRightIcon
                          fontSize="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          color: 'text.secondary',
                          lineHeight: 1,
                        }}
                      >
                        {section.toUpperCase()} ({grantedInSection}/
                        {items.length})
                      </Typography>
                    </Box>
                    <Collapse in={!collapsed} timeout={200} unmountOnExit>
                      <Stack spacing={0.25}>
                        {items.map((a) => {
                          const slug = a.actionName ?? '';
                          if (!slug) return null;
                          const wasGranted = baselineActions.has(slug);
                          const nowGranted = selected.has(slug);
                          const changedAdd = !wasGranted && nowGranted;
                          const changedRemove = wasGranted && !nowGranted;
                          return (
                            <Box
                              key={slug}
                              onClick={() => toggleAction(slug)}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                py: 0.5,
                                px: 1,
                                cursor: 'pointer',
                                borderRadius: 1,
                                borderLeft: '3px solid',
                                borderLeftColor: changedAdd
                                  ? 'success.main'
                                  : changedRemove
                                    ? 'error.main'
                                    : 'transparent',
                                backgroundColor: changedAdd
                                  ? 'rgba(107,165,57,0.12)'
                                  : changedRemove
                                    ? 'rgba(197,30,70,0.12)'
                                    : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                color="success"
                                checked={nowGranted}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleAction(slug)}
                                sx={{
                                  p: 0,
                                  mt: '2px',
                                  // The Athena theme (shared-theme/customizations/inputs.jsx)
                                  // hardcodes MuiCheckbox's Mui-checked state to
                                  // Athena Deep Blue (brand[500]), overriding the
                                  // `color="success"` prop. Re-override here to
                                  // paint the checked box green so it matches the
                                  // ✓ affordance used in the read-only permissions
                                  // view from PR #1674.
                                  '&.Mui-checked': {
                                    backgroundColor: 'success.main',
                                    borderColor: 'success.main',
                                    '&:hover': {
                                      backgroundColor: 'success.dark',
                                      borderColor: 'success.dark',
                                    },
                                  },
                                }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {slug}
                                </Typography>
                                {a.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', lineHeight: 1.35 }}
                                  >
                                    {a.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          )}

          {saveError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setSaveError(null)}
            >
              {saveError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip
            title={
              roleHasSlash
                ? 'Blocked by the chi decoded-slash bug — see warning above'
                : !hasChanges
                  ? 'No changes to save'
                  : ''
            }
            disableHoverListener={!roleHasSlash && hasChanges}
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !hasChanges || roleHasSlash}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {saving
                  ? 'Saving…'
                  : hasChanges
                    ? `Save ${diff.added.length + diff.removed.length} change${diff.added.length + diff.removed.length === 1 ? '' : 's'}`
                    : 'Save'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSaveSuccess(false)}
        >
          Role permissions updated
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditRolePermissionsDialog;
